/**
 * ChatInput Component
 * Input area for the chatbot with channel selection, file attachments, and action buttons
 */

import { useState, useRef, useEffect } from 'react';
import type { KeyboardEvent, ChangeEvent, DragEvent } from 'react';
import { Paperclip, Zap, MoveRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ChatInputProps } from '../types';
import type { AttachmentMeta } from '@/types/websocket.types';
import { CHANNEL_ICONS } from '@/modules/behaviour/constants';
import { AttachmentPreviewBar } from './attachment-preview-bar';

const MAX_ATTACHMENTS = 10;
const ACCEPTED_TYPES = 'image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx';

export const ChatInput = ({
  onSend,
  onToggleSuggestions,
  placeholder = 'Ask Kim AI...',
  disabled = false,
  selectedChannelId,
  availableChannels = [],
  onChannelChange
}: ChatInputProps) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<AttachmentMeta[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const addFilesAsAttachments = (files: File[]) => {
    const remaining = MAX_ATTACHMENTS - attachments.length;
    const newAttachments: AttachmentMeta[] = files.slice(0, remaining).map(f => ({
      name: f.name,
      content_type: f.type || undefined,
      size: f.size,
    }));
    if (newAttachments.length > 0) {
      setAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addFilesAsAttachments(files);
    e.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    const hasText = message.trim().length > 0;
    const hasAttachments = attachments.length > 0;
    if ((hasText || hasAttachments) && !disabled) {
      onSend(message.trim(), hasAttachments ? attachments : undefined);
      setMessage('');
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleToggleSuggestions = () => {
    setSuggestionsEnabled(!suggestionsEnabled);
    onToggleSuggestions?.();
  };

  // Drag-and-drop handlers
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFilesAsAttachments(files);
    }
  };

  const canSend = !disabled && (message.trim().length > 0 || attachments.length > 0);

  return (
    <div className='relative w-full'>
      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept={ACCEPTED_TYPES}
        onChange={handleFilesSelected}
        className='hidden'
      />
      <div
        className={cn(
          'relative flex flex-col items-end gap-1.5 pb-2.5 pt-4 px-2.5 bg-white rounded-[20px] transition-shadow',
          isDragOver && 'ring-2 ring-purple-400 ring-offset-1'
        )}
        style={{
          borderColor: 'var(--color-purple-50)',
          boxShadow: '0 0 12px 1px #A6A6FF, 0 0 12px 0 #E8BDFF inset'
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Textarea */}
        <div className='flex min-w-full'>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={2}
            className={cn(
              'w-full px-2 resize-none border-0 bg-transparent',
              'text-xs placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-0',
              'max-h-32 overflow-y-auto',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
            style={{ lineHeight: '1.5' }}
          />
        </div>

        {/* Attachment Preview */}
        <AttachmentPreviewBar
          attachments={attachments}
          onRemove={handleRemoveAttachment}
        />

        <div className='flex justify-between items-center w-full'>
          {/* Channel Selector */}
          {availableChannels.length > 0 && (
            <div className='shrink-0'>
              <Tabs value={selectedChannelId} onValueChange={onChannelChange}>
                <TabsList className='h-min bg-muted gap-1 p-1 border-none'>
                  {availableChannels.map((channel) => {
                    const channelType = channel.name.toLowerCase() as keyof typeof CHANNEL_ICONS;
                    const iconConfig = CHANNEL_ICONS[channelType];
                    const isSelected = selectedChannelId === channel.id;
                    const iconSrc = iconConfig ? (isSelected ? iconConfig.active : iconConfig.inactive) : undefined;

                    return (
                      <TooltipProvider key={channel.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <TabsTrigger
                              value={channel.id}
                              className={cn(
                                'h-6 w-8 p-0 rounded-full',
                                isSelected ? 'bg-white drop-shadow-sm' : 'bg-transparent',
                                'hover:bg-white transition-colors'
                              )}
                            >
                              {iconSrc ? (
                                <img src={iconSrc} alt={channel.name} />
                              ) : (
                                <span className='text-xs'>{channel.name.slice(0, 2)}</span>
                              )}
                            </TabsTrigger>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className='capitalize'>{channel.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex items-center gap-1 shrink-0'>
            {/* Attach Button */}
            <Button
              variant='ghost'
              size='icon'
              onClick={handleAttachClick}
              disabled={disabled || attachments.length >= MAX_ATTACHMENTS}
              className='h-8 w-8 text-muted-foreground hover:text-foreground'
              title='Attach file'
            >
              <Paperclip className='h-4 w-4' />
            </Button>

            {/* Toggle Suggestions Button */}
            {onToggleSuggestions && (
              <Button
                variant='ghost'
                size='icon'
                onClick={handleToggleSuggestions}
                disabled={disabled}
                className={cn(
                  'h-7 w-7 rounded-full border shadow',
                  suggestionsEnabled ? 'text-purple-accent hover:text-purple-700' : 'text-muted-foreground hover:text-foreground'
                )}
                title={suggestionsEnabled ? 'Hide suggestions' : 'Show suggestions'}
              >
                <Zap className='h-4 w-4' />
              </Button>
            )}

            {/* Send Button */}
            <Button
              variant='ghost'
              size='icon'
              onClick={handleSend}
              disabled={!canSend}
              className='h-8 w-8 rounded-full border border-black-100 bg-black-500 text-white disabled:opacity-30'
              title='Send message'
            >
              <MoveRight className='h-4 w-4' />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
